// ── Partner Logos Manager ──────────────────────────────────────────────────────
// Centralized management of partner/collaborator logos
// Each logo: name, image (upload/URL), display locations (cover/cases/all)

import { Plus, Trash2, Building2 } from 'lucide-react';
import ImageUploader from './ImageUploader';
import { uid } from '../../lib/uid';


export default function PartnerLogosManager({ data, onChange }) {
  const logos = data || [];

  const addLogo = () => {
    const newLogo = {
      id: uid(),
      name: '',
      image_url: '',
      source_type: 'placeholder',
      display_in: 'all', // 'cover' | 'cases' | 'all'
    };
    onChange([...logos, newLogo]);
  };

  const removeLogo = (idx) => {
    const updated = [...logos];
    updated.splice(idx, 1);
    onChange(updated);
  };

  const updateLogo = (idx, field, value) => {
    const updated = [...logos];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {/* Logo list */}
      {logos.length === 0 ? (
        <div className="text-center py-8 bg-[#f8f7f4] border border-[#e5e4df] rounded-lg">
          <Building2 className="w-10 h-10 mx-auto text-[#c9a84c]/30 mb-3" />
          <p className="text-sm text-[#6b7280] mb-1">No partner logos yet</p>
          <p className="text-xs text-[#9ca3af]">Add logos to display on the cover and project case pages</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logos.map((logo, idx) => (
            <div key={logo.id} className="flex items-start gap-3 p-3 bg-white border border-[#e5e4df] rounded-lg">
              {/* Logo image */}
              <div className="flex-shrink-0">
                <ImageUploader
                  value={logo.image_url}
                  sourceType={logo.source_type || 'placeholder'}
                  onChange={({ url, sourceType }) => {
                    updateLogo(idx, 'image_url', url);
                    updateLogo(idx, 'source_type', sourceType);
                  }}
                  onRemove={() => {
                    updateLogo(idx, 'image_url', '');
                    updateLogo(idx, 'source_type', 'placeholder');
                  }}
                  aspectRatio="2:1"
                  height="60px"
                  compact
                />
              </div>

              {/* Logo details */}
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={logo.name || ''}
                  onChange={(e) => updateLogo(idx, 'name', e.target.value)}
                  placeholder="Partner name"
                  className="w-full px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white mb-1.5"
                />
                {/* Display location */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#6b7280]">Show in:</span>
                  {['all', 'cover', 'cases'].map(loc => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => updateLogo(idx, 'display_in', loc)}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                        logo.display_in === loc
                          ? 'border-[#c9a84c] bg-[#c9a84c]/10 text-[#1a1a2e] font-semibold'
                          : 'border-[#e5e4df] text-[#6b7280] hover:border-[#c9a84c]/50'
                      }`}
                    >
                      {loc === 'all' ? 'All' : loc === 'cover' ? 'Cover' : 'Cases'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => removeLogo(idx)}
                className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                title="Remove logo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add button */}
      <button
        type="button"
        onClick={addLogo}
        className="flex items-center gap-1.5 w-full py-2 text-sm font-medium text-[#c9a84c] hover:text-[#b8973e] border border-dashed border-[#c9a84c]/40 rounded-lg hover:bg-[#c9a84c]/5 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Partner Logo
      </button>
    </div>
  );
}
